import React, {ReactElement, useId} from 'react';
import Pagination, {PaginationProps} from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import {t} from 'i18next';
import {AurDropdownButton} from '../aur-button/AurDropdownButton';
import {ReactComponent as BackIcon} from '../../../../src/assests/svg/back-icon.svg';
import {ReactComponent as BackIconDisabled} from '../../../../src/assests/svg/back-icon-disabled.svg';
import {ReactComponent as RightIcon} from '../../../assests/svg/right-icon.svg';
import {ReactComponent as RightIconDisabled} from '../../../assests/svg/right-icon-disabled.svg';
import {ReactComponent as OrderTableColumnIcon} from '../../../assests/svg/order-table-column.svg';
import {ReactComponent as DropDownIcon} from '../../../../src/assests/svg/drop_down.svg';

import AurButton from '../aur-button/AurButton';
import {PaginationItem} from '@mui/material';
import AurIcon, {IconNames} from '../aur-icon/AurIcon';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
} from '../../../constants/analyticsConstants';
import {useAnalytics} from '../../../hooks/useAnalytics';

// Define the props for the AurPagination component, extending from PaginationProps
type AurPaginationProps = PaginationProps & {
  totalItems: number; // Total number of items to paginate through
  itemsPerPage: number; // Number of items per page
  currentPage: number; // Current active page
  perPageOptions?: number[]; // Optional array of items per page options
  isWideUseVariant?: boolean; // Optional boolean to toggle wide variant
  backgroundColor?: string; // Optional background color for the pagination box
  itemType?: string; // Type of items being paginated (e.g., "items")
  onPageChange: (page: number) => void; // Function to handle page change
  setItemsPerPage: (itemsPerPage: number) => void; // Function to set items per page
  includeCancelContinueEvents?: boolean;
  handleContinueClick?: () => void; // Function to set items per page
  handleCancelClick?: () => void; // Function to set items per page
  canClearAriaLabel?: string;
  continuButtonDisabled?: boolean;
  continueButtonHint?: string;
  leftSidePageCaptionVisible?: boolean;
  className?: string;
  renderLeftComponent?: () => ReactElement | ReactElement[];
  reorderColumnButtonVisible?: boolean;
  handleReorderColumnClick?: () => void;
  ariaLabelNextPage?: string;
  ariaLabelPreviousPage?: string;
  ariaDescribedByNextPage?: string;
  ariaDescribedByPreviousPage?: string;
  appliedFilterCount?: number;
  onClearFilterClick?: () => void;
  pageItemDropDownItemColor?: string;
  trackPaginationActionName?: string;
  paginationAriaLabel?: string;
  gaRegion?: string;
};

// AurPagination functional component definition
const AurPagination: React.FC<AurPaginationProps> = ({
  totalItems,
  itemsPerPage,
  currentPage,
  isWideUseVariant = true,
  backgroundColor = '#121A25',
  itemType,
  perPageOptions = [25, 50, 100],
  onPageChange,
  setItemsPerPage,
  handleCancelClick,
  handleContinueClick,
  includeCancelContinueEvents = false,
  continuButtonDisabled,
  canClearAriaLabel,
  continueButtonHint,
  leftSidePageCaptionVisible = true,
  className,
  renderLeftComponent,
  reorderColumnButtonVisible = false,
  handleReorderColumnClick,
  ariaLabelNextPage,
  ariaLabelPreviousPage,
  ariaDescribedByNextPage,
  ariaDescribedByPreviousPage,
  onClearFilterClick,
  appliedFilterCount,
  pageItemDropDownItemColor,
  trackPaginationActionName = t('gaTracking.global.reportPagination'),
  paginationAriaLabel,
  gaRegion = GA_REGIONS.FOOTER,
  'aria-label': suppliedAriaLabel,
  ...rest
}) => {
  const {trackGAEvent} = useAnalytics();
  // Calculate the total number of pages based on totalItems and itemsPerPage
  const totalPages = Math.ceil((totalItems ?? 0) / itemsPerPage);
  const reactId = useId();

  // Function to handle the click event for previous or next page buttons
  const handleOnPreviousPageClick = (isPrevious: boolean) => {
    onPageChange(isPrevious ? currentPage - 1 : currentPage + 1);
  };

  const trackPagination = (name: string) => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: name,
        type: GA_CLICK_TYPES.LINK,
        region: gaRegion,
        action: trackPaginationActionName,
      },
    });
  };

  const navigationLabel =
    paginationAriaLabel || suppliedAriaLabel || t('common.paginationContainer');

  return (
    // Main container with customizable background color
    <Box
      bgcolor={backgroundColor}
      className={`aurPagination ${className ?? ''}`}
      data-testid="aurPagination"
    >
      {renderLeftComponent ? renderLeftComponent() : null}
      {/* Conditionally render the status text showing the range of items displayed */}
      {includeCancelContinueEvents || !leftSidePageCaptionVisible ? (
        ''
      ) : (
        <Typography
          component="p"
          className="typo-text body3_size status"
          aria-live="polite"
          id="pagination-status"
          role="status"
          aria-atomic="true"
        >
          <span>
            {isWideUseVariant &&
              `${t('common.pagination.showing')} ${totalItems === 0 ? 0 : itemsPerPage * (currentPage - 1) + 1} ${t('common.pagination.to')}  ${Math.min(itemsPerPage * currentPage, totalItems)} ${t('common.pagination.of')} ${totalItems} ${itemType}`}
          </span>
        </Typography>
      )}
      {/* Flex container to align pagination controls */}
      <Box
        display="flex"
        justifyContent={`${includeCancelContinueEvents ? 'left' : 'right'}`}
        alignItems="center"
      >
        <Stack
          spacing={1}
          style={{
            paddingRight: '10px',
          }}
        >
          {/* Dropdown button to select items per page */}
          <AurDropdownButton
            elementID="per-page-selection-dropdown"
            buttonText={`${t('common.pagination.perPage')}: ${itemsPerPage}`}
            type="text"
            endIcon={<DropDownIcon aria-hidden="true" />}
            aria-live="polite"
            size="Small"
            iconType="Arrow"
            menuDirectionLeft={false}
            aria-haspopup="true"
            aria-expanded="false"
            aria-controls="perPageOptions"
            menuItemHeight="small"
            menuItemWidth="small"
            menuItemTextSize="small"
            classNames="pagination-dropdown-button-icon"
            // Generate menu items based on perPageOptions array
            menuItems={perPageOptions.map((option) => {
              return {
                label: option.toString(),
                onClick: () => {
                  setItemsPerPage(option); // Update items per page on click
                  trackPagination(
                    `${option + ' ' + t('common.pagination.perPage')}`
                  );
                },
              };
            })}
            menuItemTextColor={pageItemDropDownItemColor}
          />
        </Stack>
        {totalPages > 1 && (
          <>
            {/* Label for pagination */}
            <Typography className="typo-text body3_size page" component="span">
              {t('common.pagination.pages')}
            </Typography>
            <Stack spacing={1}>
              {/* Pagination component for navigating pages */}
              <Pagination
                count={totalPages} // Total number of pages
                page={currentPage} // Current active page
                onChange={(_, page) => {
                  onPageChange(page); // Handle page change
                  trackPagination(`${page}`);
                }}
                variant="outlined"
                shape="rounded"
                color="primary"
                hideNextButton={true} // Hide default next button
                hidePrevButton={true} // Hide default previous button
                aria-label={navigationLabel}
                renderItem={(item) => (
                  <PaginationItem
                    {...item}
                    id={`pagination-${reactId}-${item.type}-${item.page ?? 'item'}`}
                    aria-label={`page:${item.page}`}
                    aria-current={
                      item.page === currentPage ? 'page' : undefined
                    }
                  />
                )}
                {...rest}
              />
            </Stack>
            <Stack
              style={{
                paddingLeft: '10px',
                paddingRight: '4px',
              }}
            >
              <AurButton
                onClick={() => {
                  handleOnPreviousPageClick(true);
                  trackPagination(t('gaTracking.pagination.leftArrow'));
                }} // Navigate to previous page
                minWidth={48}
                tabIndex={0}
                type="exit"
                size="Small"
                disabled={currentPage === 1} // Disable button if on first page
                ariaLabel={
                  ariaLabelPreviousPage ||
                  `${t('common.pagination.pagingPrevious')} ${currentPage === 1 ? t('common.pagination.inactive') : t('common.pagination.active')} `
                }
                ariaDescribedBy={
                  ariaDescribedByPreviousPage ? ariaDescribedByPreviousPage : ''
                }
                aria-disabled={currentPage === 1}
              >
                {currentPage === 1 ? (
                  <BackIconDisabled aria-hidden="true" />
                ) : (
                  <BackIcon aria-hidden="true" />
                )}
              </AurButton>
            </Stack>
            {/* Custom next page button */}
            <Stack>
              <AurButton
                onClick={() => {
                  handleOnPreviousPageClick(false);
                  trackPagination(t('gaTracking.pagination.rightArrow'));
                }} // Navigate to next page
                minWidth={48}
                type="exit"
                size="Small"
                tabIndex={0}
                disabled={currentPage === totalPages} // Disable button if on last page
                ariaLabel={
                  ariaLabelNextPage ||
                  `${t('common.pagination.pagingNext')} ${currentPage === totalPages ? t('common.pagination.inactive') : t('common.pagination.active')} `
                }
                ariaDescribedBy={
                  ariaDescribedByNextPage ? ariaDescribedByNextPage : ''
                }
                aria-disabled={currentPage === totalPages}
              >
                {currentPage === totalPages ? (
                  <RightIconDisabled aria-hidden="true" />
                ) : (
                  <RightIcon aria-hidden="true" />
                )}
              </AurButton>
            </Stack>
          </>
        )}
        {reorderColumnButtonVisible && (
          <Stack className="reorder-button-container">
            <AurButton
              onClick={handleReorderColumnClick}
              minWidth={32}
              type="exit"
              size="Small"
              classNames="order-icon-button"
              tabIndex={0}
              ariaLabel={t('common.pagination.changeOrderBtnAriaLabel')}
            >
              <OrderTableColumnIcon aria-hidden="true" />
            </AurButton>
          </Stack>
        )}
        {(appliedFilterCount ?? 0) > 0 && (
          <Stack className="clear-filter-button-container">
            <AurButton
              type="exit"
              size="Small"
              startIcon={<AurIcon icon={IconNames.ClearFiltersIcon} />}
              label={`${t('fieldManagement.fieldTable.clearFilters')} (${appliedFilterCount})`}
              onClick={onClearFilterClick}
              dataTestId="clear-filter-button"
              ariaLabel={t('common.pagination.clearFiltersHint')}
              tabIndex={0}
            />
          </Stack>
        )}
      </Box>
      {includeCancelContinueEvents && (
        <div
          style={{
            display: 'flex',
            gap: '24px',
          }}
        >
          <Stack>
            <AurButton
              onClick={handleCancelClick}
              type="exit"
              size="Medium"
              label={t('common.cancel')}
              classNames="pagination-cancel-button"
              ariaLabel={canClearAriaLabel}
              dataTestId="pagination-cancel-button"
              tabIndex={0}
            />
          </Stack>
          {/* Custom next page button */}
          <Stack>
            <AurButton
              onClick={handleContinueClick}
              type="continue"
              size="Medium"
              label={t('common.continue')}
              disabled={continuButtonDisabled}
              classNames="pagination-continue-button"
              dataTestId="pagination-continue-button"
              ariaLabel={continueButtonHint}
              tabIndex={0}
            />
          </Stack>
        </div>
      )}
    </Box>
  );
};

export default AurPagination;
